// The verified-credential card (docs/design/slice4-certify-ux.md §2.5, §5.2) - a
// founder-uploaded real certificate. STRUCTURALLY DISTINCT from `FrameworkResultCard`
// (§2.5): never shares a border, background, or heading with the readiness block, even
// for the same `framework`/`scope` - "assessed"/"would qualify" language and verbs stay
// on the readiness side; this card uses its own closed `VerifyState` vocabulary
// (`unverified | verifying | verified | failed`, landed `Certificate.verifyState`,
// packages/shared/src/model/certify.ts:166-190) and its own document/seal glyph, never
// the risk/warn/good `.status-chip` palette used for a pass/fail chip 1:1 read.
import type { Certificate } from "@nightcrew/shared";
import { certifyCopy, type CertifyCopyKey } from "../copy/certify.js";
import { CertificateDistinctionNote } from "./SelfAssessmentDisclosure.js";

const VERIFY_STATE_CHIP: Record<Certificate["verifyState"], string> = {
  unverified: "status-chip neutral",
  verifying: "status-chip warn",
  verified: "status-chip good",
  failed: "status-chip risk",
};

const VERIFY_STATE_COPY_KEY: Record<Certificate["verifyState"], CertifyCopyKey> = {
  unverified: "certify.dataRoom.verifyState.unverified",
  verifying: "certify.dataRoom.verifyState.verifying",
  verified: "certify.dataRoom.verifyState.verified",
  failed: "certify.dataRoom.verifyState.failed",
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** The expiry-watch chip (D46 "expiry tracked by the watch", §5.2): independent of
 * verify-state - a `verified` certificate can still be expiring; an `unverified` or
 * `verifying` one can too - both render side by side, never one implying the other. */
export const ExpiryWatchChip = ({
  expiresAt,
  now = Date.now(),
}: {
  readonly expiresAt: string;
  readonly now?: number;
}): React.JSX.Element | null => {
  const expiresMs = Date.parse(expiresAt);
  // Fail closed on a non-finite parse (app-engineering §2): treat as expired-unknown
  // rather than silently rendering a healthy chip on garbage input.
  if (!Number.isFinite(expiresMs)) {
    return <span className="status-chip risk">{certifyCopy("certify.dataRoom.verifyState.failed")}</span>;
  }
  const daysRemaining = Math.ceil((expiresMs - now) / DAY_MS);
  if (expiresMs <= now) {
    return (
      <span className="status-chip risk" data-testid="expiry-watch" data-band="expired">
        {certifyCopy("certify.dataRoom.expiryWatch.expired", {
          date: new Date(expiresAt).toLocaleDateString(),
        })}
      </span>
    );
  }
  if (daysRemaining <= 30) {
    return (
      <span className="status-chip warn" data-testid="expiry-watch" data-band="warning">
        {certifyCopy("certify.dataRoom.expiryWatch.expiresIn", { days: daysRemaining })}
      </span>
    );
  }
  // W2 fix (design-review): the healthy band previously repeated the SAME expiry date
  // already shown, labeled, in CredentialCard's own "Expires: {date}" chip just above -
  // a redundant, unlabeled bare date. The watch chip now only surfaces once there is
  // something to watch (warning/expired); a healthy certificate shows no second date.
  return null;
};

export const CredentialCard = ({
  certificate,
  appName,
  onOpen,
}: {
  readonly certificate: Certificate;
  readonly appName: string;
  readonly onOpen?: () => void;
}): React.JSX.Element => (
  <div className="card pad credential-card" data-testid="credential-card" data-verify-state={certificate.verifyState}>
    <div className="row between aic">
      <div className="row aic gap8">
        <span className="credential-glyph" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
            <rect x="4" y="3" width="16" height="18" rx="2" />
            <path d="M8 8h8M8 12h8M8 16h5" />
          </svg>
        </span>
        <b style={{ fontSize: 14 }}>{certificate.issuer}</b>
      </div>
      <span className={VERIFY_STATE_CHIP[certificate.verifyState]}>
        {certifyCopy(VERIFY_STATE_COPY_KEY[certificate.verifyState])}
      </span>
    </div>
    <p className="muted mt8" style={{ fontSize: 12.5 }}>
      {certificate.scope}
    </p>
    <div className="row wrap-g gap8 mt8">
      <span className="chip">
        {certifyCopy("certify.dashboard.certificate.field.issuedDate")}: {new Date(certificate.issuedAt).toLocaleDateString()}
      </span>
      <span className="chip">
        {certifyCopy("certify.dashboard.certificate.field.expiryDate")}: {new Date(certificate.expiresAt).toLocaleDateString()}
      </span>
      <ExpiryWatchChip expiresAt={certificate.expiresAt} />
    </div>
    {certificate.verifyState === "verified" ? (
      <div className="mt12">
        <p style={{ margin: 0, fontSize: 12.5 }}>
          {certifyCopy("certify.dashboard.certificate.verifiedDetail", {
            issuer: certificate.issuer,
            scope: certificate.scope,
            issuedDate: new Date(certificate.issuedAt).toLocaleDateString(),
            expiryDate: new Date(certificate.expiresAt).toLocaleDateString(),
          })}
        </p>
        <div className="mt8">
          <CertificateDistinctionNote appName={appName} issuer={certificate.issuer} />
        </div>
      </div>
    ) : null}
    {onOpen ? (
      <button type="button" className="btn btn-ghost btn-sm mt12" onClick={onOpen}>
        View evidence
      </button>
    ) : null}
  </div>
);
